import { useState } from 'react';
import type { Answers, ResultContent, Selection } from '@/features/fanbase-fix/types';
import { categoryLabels, playbooks } from '@/features/fanbase-fix/playbooks';
import { overviewLabels } from '@/features/fanbase-fix/selector';
import { assetToText, fullPlanToText } from '@/features/fanbase-fix/planText';
import { CopyButton } from './CopyButton';

interface Props {
  answers: Answers;
  selection: Selection;
  result: ResultContent;
  onCorrect?: () => void;
  onSupport?: () => void;
}

export function ResultView({ answers, selection, result, onCorrect, onSupport }: Props) {
  const secondaries = selection.secondaries.map((id) => playbooks[id]);
  const [detailOpen, setDetailOpen] = useState(false);

  return (
    <article className="mx-auto max-w-2xl px-6 pb-24 lg:px-0">
      {/* Opening statement */}
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/40">Your Fanbase Fix</p>

      <h1 className="mt-5 font-headline text-[clamp(2rem,6vw,3.25rem)] font-bold leading-[1.05] text-white">
        {result.headline}
      </h1>

      <p className="mt-6 text-lg leading-relaxed text-white/80">{result.startHereBody}</p>
      {result.provisionalNote && <p className="mt-3 text-[15px] leading-relaxed text-white/50">{result.provisionalNote}</p>}

      {/* Why, in their own answers */}
      <div className="mt-12">
        <h2 className="font-headline text-xl font-bold text-white">Why this one first</h2>
        <p className="mt-3 text-[15px] leading-relaxed text-white/70">
          This is what you told us, and it is the whole reason this came first.
        </p>
        <ul className="mt-4 space-y-2.5">
          {result.basis.map((b, i) => (
            <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-white/75">
              <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-[#1DB954]" aria-hidden />
              <span>{b.paraphrase}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* The one thing to take away */}
      <div className="mt-12 rounded-2xl bg-white/[0.045] p-6 sm:p-8">
        <h2 className="font-headline text-xl font-bold text-white sm:text-2xl">{result.asset.title}</h2>
        {result.asset.readiness !== 'READY_TO_USE' && (
          <p className="mt-2 text-sm text-white/50">Check the marked details before you use this.</p>
        )}

        <div className="mt-6 space-y-5">
          {result.asset.blocks.map((b, i) => (
            <div key={i}>
              <p className="text-sm font-semibold text-white">{b.heading}</p>
              <p className="mt-1 whitespace-pre-line text-[15px] leading-relaxed text-white/75">{b.body}</p>
            </div>
          ))}
        </div>

        {result.asset.prerequisiteChecks.length > 0 && (
          <div className="mt-6">
            <p className="text-sm font-semibold text-white/80">Before you use this</p>
            <ul className="mt-2 space-y-1 text-sm leading-relaxed text-white/55">
              {result.asset.prerequisiteChecks.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </div>
        )}

        {result.missingInputs.length > 0 && (
          <p className="mt-5 text-sm text-white/45">
            You skipped some detail, so parts of this are a starting version rather than something written around
            your specifics.
          </p>
        )}

        <CopyButton className="mt-7" text={assetToText(result.asset)} label={result.asset.copyButtonLabel} />
      </div>

      {/* Do this, watch this, then what */}
      <div className="mt-12 space-y-5 text-[15px] leading-relaxed text-white/80">
        <h2 className="font-headline text-xl font-bold text-white">Do this first</h2>
        <p>
          {result.firstAction.instruction}{' '}
          <span className="text-white/45">
            About {result.firstAction.approximateEffortMinutes} minutes, including preparation.
          </span>
        </p>
        <p>
          <span className="text-white">Watch for:</span> {result.measurement.mainObservation} Give it{' '}
          {result.measurement.window.toLowerCase()}.
        </p>
        <p>
          <span className="text-white">Do it again</span> when {lower(result.decisionRule.repeatWhen)}{' '}
          <span className="text-white">Change it</span> when {lower(result.decisionRule.adjustWhen)}
        </p>
        <p className="text-white/55">
          If neither happens clearly, you do not have enough to judge yet: {lower(result.decisionRule.insufficientInformationWhen)}
        </p>
        <p className="text-white/55">{result.measurement.limitation}</p>
      </div>

      <div className="mt-12 border-t border-white/10 pt-8">
        <CopyButton text={fullPlanToText(result, selection)} label="COPY MY FULL PLAN" />
      </div>

      {/* Quiet detail */}
      <div className="mt-8 border-t border-white/10 pt-8 text-[15px] leading-relaxed text-white/60">
        <p>
          <span className="text-white/85">Leave for now:</span> {result.whatCanWait.activity}{' '}
          {result.whatCanWait.reason}
        </p>

        {secondaries.length > 0 && (
          <div className="mt-5">
            <p className="text-white/85">Once that test has run, look at:</p>
            <ul className="mt-2 space-y-1.5">
              {secondaries.map((s) => (
                <li key={s.id}>
                  {categoryLabels[s.category]} &mdash; {s.name.toLowerCase()}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-white/40">Two big changes at the same time cannot be read.</p>
          </div>
        )}

        <button
          type="button"
          onClick={() => setDetailOpen((v) => !v)}
          className="mt-6 text-sm text-white/50 underline underline-offset-4 transition-colors hover:text-white"
        >
          {detailOpen ? 'Hide the rest of your answers' : 'See where everything else sits'}
        </button>

        {detailOpen && (
          <ul className="mt-4 space-y-3">
            {(Object.keys(selection.overview) as (keyof typeof selection.overview)[]).map((cat) => {
              const o = selection.overview[cat];
              return (
                <li key={cat}>
                  <span className="text-white/85">{categoryLabels[cat]}</span>{' '}
                  <span className="text-white/40">&middot; {overviewLabels[o.state].toLowerCase()}</span>
                  <p className="text-white/55">{o.note}</p>
                </li>
              );
            })}
          </ul>
        )}

        {onCorrect && (
          <p className="mt-6">
            <button
              type="button"
              onClick={onCorrect}
              className="text-sm text-white/50 underline underline-offset-4 transition-colors hover:text-white"
            >
              This doesn&apos;t sound like my situation
            </button>
          </p>
        )}

        <p className="mt-6 text-sm text-white/45">
          Want help putting this into practice?{' '}
          {onSupport ? (
            <button
              type="button"
              onClick={onSupport}
              className="text-white underline underline-offset-4 transition-colors hover:text-white/70"
            >
              Tell the DK team what you&apos;re working on
            </button>
          ) : (
            <span className="text-white/40">Asking the DK team isn&apos;t connected yet.</span>
          )}{' '}
          Your plan is yours either way.
        </p>

        <p className="mt-6 text-xs leading-relaxed text-white/35">
          Prepared from the information you supplied using DK&apos;s framework. Your audio and private accounts
          have not been reviewed.
          {answers.songTitle ? '' : ' No song title was supplied, so none has been invented.'}
        </p>
      </div>
    </article>
  );
}

const lower = (s: string) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
